import { useState, useMemo, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  TrendingUp,
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  Building2,
  Target,
  Layers,
  RefreshCw,
  MapPin,
  Calendar,
  ZoomIn,
  Check,
  X,
} from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
  Cell,
  ReferenceLine,
} from 'recharts';
import {
  useProductSalesTrendReport,
  useProductSalesTrendDaily,
  useProductSalesTrendHourly,
  useProductHierarchyReport,
  useRegionalSalesReport,
  useSalesTargetReport,
} from '../../api/queries/analytics.queries';
import { useBranches } from '../../api/queries/transfer.queries';
import { useAuthStore } from '../../stores/auth.store';

// Report tabs configuration
const REPORT_TABS = [
  { id: 'product-trend', label: 'Product Sales Trend', labelTh: 'แนวโน้มการขายสินค้า', icon: TrendingUp },
  { id: 'product-hierarchy', label: 'Product Hierarchy', labelTh: 'ลำดับชั้นสินค้า', icon: Layers },
  { id: 'regional-sales', label: 'Regional Sales', labelTh: 'ยอดขายตามภูมิภาค', icon: MapPin },
  { id: 'sales-target', label: 'Sales Target', labelTh: 'เป้าหมายยอดขาย', icon: Target },
] as const;

type ReportType = typeof REPORT_TABS[number]['id'];

// Status indicator component
function StatusIndicator({ status }: { status: 'above' | 'below' | 'equal' }) {
  const colors = {
    above: 'bg-green-500',
    below: 'bg-red-500',
    equal: 'bg-yellow-500',
  };
  return <span className={`inline-block w-3 h-3 rounded-full ${colors[status]}`} />;
}

// Achievement status indicator
function AchievementStatus({ status }: { status: 'high' | 'medium' | 'low' }) {
  const colors = {
    high: 'bg-green-500',
    medium: 'bg-yellow-500',
    low: 'bg-red-500',
  };
  return <span className={`inline-block w-3 h-3 rounded-full ${colors[status]}`} />;
}

// Achievement badge with percentage
function AchievementBadge({ value, size = 'md' }: { value: number; size?: 'sm' | 'md' }) {
  const achieved = value >= 100;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 ${
      achieved ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
    } ${size === 'sm' ? 'text-xs' : 'text-sm'}`}>
      {value.toFixed(1)}%
      {achieved ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}
    </span>
  );
}

// Growth indicator
function GrowthIndicator({ growth }: { growth: { percentage: number; direction: string } }) {
  const color = growth.direction === 'up' ? 'text-green-600' : growth.direction === 'down' ? 'text-red-600' : 'text-gray-500';
  const arrow = growth.direction === 'up' ? '↑' : growth.direction === 'down' ? '↓' : '→';

  return (
    <span className={`font-medium ${color}`}>
      {arrow} {Math.abs(growth.percentage).toFixed(1)}%
    </span>
  );
}

function PerformanceAnalyticsPage() {
  const { i18n } = useTranslation();
  const user = useAuthStore((state) => state.user);
  const isHQ = user?.role === 'HQ_ADMIN';
  const currentLang = i18n.language as 'en' | 'th';

  const [activeTab, setActiveTab] = useState<ReportType>('product-trend');
  const [selectedPeriod, setSelectedPeriod] = useState<string>('last-12'); // Default to Last 12 Months
  const [selectedBranch, setSelectedBranch] = useState<string>('');
  const [selectedDistrict, setSelectedDistrict] = useState<string>('');
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());

  // Period options
  const periodOptions = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const options: Array<{ value: string; label: string; labelTh: string; group: 'preset' | 'year' }> = [
      { value: 'last-6', label: 'Last 6 Months', labelTh: '6 เดือนล่าสุด', group: 'preset' },
      { value: 'last-12', label: 'Last 12 Months', labelTh: '12 เดือนล่าสุด', group: 'preset' },
      { value: `year-${currentYear}`, label: `This Year (${currentYear})`, labelTh: `ปีนี้ (${currentYear})`, group: 'preset' },
      { value: `year-${currentYear - 1}`, label: `Last Year (${currentYear - 1})`, labelTh: `ปีที่แล้ว (${currentYear - 1})`, group: 'preset' },
    ];
    // Add older years
    for (let i = currentYear - 2; i >= currentYear - 4; i--) {
      options.push({ value: `year-${i}`, label: `${i}`, labelTh: `${i}`, group: 'year' });
    }
    return options;
  }, []);

  // Compute date range from selected period
  const periodDateRange = useMemo(() => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth(); // 0-indexed

    if (selectedPeriod === 'last-6') {
      // Last 6 complete months + current month
      const startDate = new Date(currentYear, currentMonth - 5, 1);
      const startStr = `${startDate.getFullYear()}-${String(startDate.getMonth() + 1).padStart(2, '0')}`;
      const endStr = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}`;
      return { year: undefined, startDate: startStr, endDate: endStr };
    }
    if (selectedPeriod === 'last-12') {
      // Last 12 months (11 past + current)
      const startDate = new Date(currentYear, currentMonth - 11, 1);
      const startStr = `${startDate.getFullYear()}-${String(startDate.getMonth() + 1).padStart(2, '0')}`;
      const endStr = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}`;
      return { year: undefined, startDate: startStr, endDate: endStr };
    }
    if (selectedPeriod.startsWith('year-')) {
      const year = parseInt(selectedPeriod.replace('year-', ''));
      return { year, startDate: undefined, endDate: undefined };
    }
    // Fallback
    return { year: currentYear, startDate: undefined, endDate: undefined };
  }, [selectedPeriod]);

  // Backward-compatible selectedYear for other tabs that still use year
  const selectedYear = periodDateRange.year || new Date().getFullYear();

  // Drill-down state for Product Sales Trend
  type DrillLevel = 'year' | 'month' | 'day';
  const [drillState, setDrillState] = useState<{
    level: DrillLevel;
    selectedMonth: number | null; // 0-11
    selectedMonthYear: number | null; // year of the selected month (for multi-year periods)
    selectedDay: number | null; // 1-31
  }>({
    level: 'year',
    selectedMonth: null,
    selectedMonthYear: null,
    selectedDay: null,
  });

  // Period selection state for Product Types table drill-down
  const [selectedPeriodIndex, setSelectedPeriodIndex] = useState<number | null>(null);
  // Expanded categories for Product Types table
  const [expandedProductCategories, setExpandedProductCategories] = useState<Set<string>>(new Set());

  // Double-click detection for chart drill-down
  // Ref to track last click time and data for double-click detection
  const lastChartClickRef = useRef<{ timestamp: number; periodIndex: number | null }>({
    timestamp: 0,
    periodIndex: null,
  });
  const DOUBLE_CLICK_THRESHOLD = 300; // ms

  // Toggle category expansion in Product Types table
  const toggleProductCategory = (categoryName: string) => {
    setExpandedProductCategories(prev => {
      const next = new Set(prev);
      if (next.has(categoryName)) {
        next.delete(categoryName);
      } else {
        next.add(categoryName);
      }
      return next;
    });
  };

  // Clear period selection
  const clearPeriodSelection = () => {
    setSelectedPeriodIndex(null);
  };

  // Reset period selection when drill level changes
  useEffect(() => {
    setSelectedPeriodIndex(null);
    setExpandedProductCategories(new Set());
  }, [drillState.level, drillState.selectedMonth, drillState.selectedDay]);

  // Chart line visibility state (for interactive legend)
  const [visibleLines, setVisibleLines] = useState<{
    sales: boolean;
    target: boolean;
    average: boolean;
  }>({
    sales: true,
    target: true,
    average: true,
  });

  // Toggle line visibility
  const toggleLineVisibility = (line: 'sales' | 'target' | 'average') => {
    setVisibleLines(prev => ({ ...prev, [line]: !prev[line] }));
  };

  // Reset drill state when period or branch filter changes
  useEffect(() => {
    setDrillState({ level: 'year', selectedMonth: null, selectedMonthYear: null, selectedDay: null });
  }, [selectedPeriod, selectedBranch]);

  // Month names for display
  const MONTH_NAMES_EN = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];
  const MONTH_NAMES_TH = ['มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน',
    'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม'];

  // Fetch branches
  const { data: branches } = useBranches();

  // Fetch reports based on active tab
  const { data: productTrendData, isLoading: loadingTrend, refetch: refetchTrend } = useProductSalesTrendReport(
    activeTab === 'product-trend' ? periodDateRange.year : undefined,
    activeTab === 'product-trend' && selectedBranch ? selectedBranch : undefined,
    activeTab === 'product-trend' ? periodDateRange.startDate : undefined,
    activeTab === 'product-trend' ? periodDateRange.endDate : undefined
  );

  // Daily drill-down data (use drilled month's year, falling back to selectedYear)
  const drillYear = drillState.selectedMonthYear ?? selectedYear;
  const { data: dailyTrendData, isLoading: loadingDaily, refetch: refetchDaily } = useProductSalesTrendDaily(
    drillYear,
    drillState.selectedMonth ?? 0,
    selectedBranch || undefined,
    activeTab === 'product-trend' && drillState.level === 'month' && drillState.selectedMonth !== null
  );

  // Hourly drill-down data
  const { data: hourlyTrendData, isLoading: loadingHourly, refetch: refetchHourly } = useProductSalesTrendHourly(
    drillYear,
    drillState.selectedMonth ?? 0,
    drillState.selectedDay ?? 1,
    selectedBranch || undefined,
    activeTab === 'product-trend' && drillState.level === 'day' && drillState.selectedMonth !== null && drillState.selectedDay !== null
  );

  const { data: hierarchyData, isLoading: loadingHierarchy, refetch: refetchHierarchy } = useProductHierarchyReport(
    activeTab === 'product-hierarchy' ? selectedYear : undefined,
    activeTab === 'product-hierarchy' && selectedBranch ? selectedBranch : undefined
  );

  const { data: regionalData, isLoading: loadingRegional, refetch: refetchRegional } = useRegionalSalesReport(
    activeTab === 'regional-sales' ? selectedYear : undefined
  );

  const { data: targetData, isLoading: loadingTarget, refetch: refetchTarget } = useSalesTargetReport(
    activeTab === 'sales-target' && selectedBranch ? selectedBranch : undefined,
    activeTab === 'sales-target' && selectedDistrict ? selectedDistrict : undefined
  );

  // Get current loading state
  const isLoading = useMemo(() => {
    switch (activeTab) {
      case 'product-trend': {
        if (drillState.level === 'year') return loadingTrend;
        if (drillState.level === 'month') return loadingDaily;
        if (drillState.level === 'day') return loadingHourly;
        return loadingTrend;
      }
      case 'product-hierarchy': return loadingHierarchy;
      case 'regional-sales': return loadingRegional;
      case 'sales-target': return loadingTarget;
      default: return false;
    }
  }, [activeTab, drillState.level, loadingTrend, loadingDaily, loadingHourly, loadingHierarchy, loadingRegional, loadingTarget]);

  // Refresh current report
  const handleRefresh = () => {
    switch (activeTab) {
      case 'product-trend': {
        if (drillState.level === 'year') refetchTrend();
        else if (drillState.level === 'month') refetchDaily();
        else if (drillState.level === 'day') refetchHourly();
        break;
      }
      case 'product-hierarchy': refetchHierarchy(); break;
      case 'regional-sales': refetchRegional(); break;
      case 'sales-target': refetchTarget(); break;
    }
  };

  // Drill-down navigation functions
  const handleDrillDown = (dataPoint: any) => {
    if (drillState.level === 'year' && dataPoint.monthIndex !== undefined) {
      // Drill from year to month (monthly to daily)
      setDrillState({
        level: 'month',
        selectedMonth: dataPoint.monthIndex,
        selectedMonthYear: dataPoint.monthYear || selectedYear,
        selectedDay: null,
      });
    } else if (drillState.level === 'month' && dataPoint.day !== undefined) {
      // Drill from month to day (daily to hourly)
      setDrillState({
        ...drillState,
        level: 'day',
        selectedDay: dataPoint.day,
      });
    }
    // At day level, no further drill-down
  };

  const handleDrillUp = () => {
    if (drillState.level === 'day') {
      setDrillState({
        ...drillState,
        level: 'month',
        selectedDay: null,
      });
    } else if (drillState.level === 'month') {
      setDrillState({
        level: 'year',
        selectedMonth: null,
        selectedMonthYear: null,
        selectedDay: null,
      });
    }
  };

  const navigateToDrillLevel = (level: DrillLevel) => {
    if (level === 'year') {
      setDrillState({ level: 'year', selectedMonth: null, selectedMonthYear: null, selectedDay: null });
    } else if (level === 'month') {
      setDrillState({ ...drillState, level: 'month', selectedDay: null });
    }
    // Day level is always the current position when clicked
  };

  // Toggle category expansion
  const toggleCategory = (categoryId: string) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(categoryId)) {
        newSet.delete(categoryId);
      } else {
        newSet.add(categoryId);
      }
      return newSet;
    });
  };

  // Currency formatter
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('th-TH', {
      style: 'currency',
      currency: 'THB',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  // Number formatter
  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('th-TH').format(num);
  };

  // Get multilang name
  const getName = (name: { en: string; th?: string } | string) => {
    if (typeof name === 'string') return name;
    return currentLang === 'th' && name.th ? name.th : name.en;
  };

  // Render report content
  const renderReportContent = () => {
    switch (activeTab) {
      // ================================
      // REPORT 1: Product Sales Trend vs Average (with drill-down)
      // ================================
      case 'product-trend': {
        // Get current data based on drill level
        const getCurrentData = () => {
          if (drillState.level === 'year') return productTrendData;
          if (drillState.level === 'month') return dailyTrendData;
          if (drillState.level === 'day') return hourlyTrendData;
          return productTrendData;
        };

        const currentData = getCurrentData();

        if (!currentData && !isLoading) {
          return <div className="py-8 text-center text-muted-foreground">No data</div>;
        }

        if (isLoading) {
          return <div className="py-8 text-center text-muted-foreground">Loading...</div>;
        }

        // Get chart config based on drill level
        const getChartConfig = () => {
          if (drillState.level === 'year') {
            return { xKey: 'month', data: productTrendData?.trend || [] };
          }
          if (drillState.level === 'month') {
            return { xKey: 'day', data: dailyTrendData?.trend || [] };
          }
          if (drillState.level === 'day') {
            return { xKey: 'hourLabel', data: hourlyTrendData?.trend || [] };
          }
          return { xKey: 'month', data: [] };
        };

        const chartConfig = getChartConfig();

        // Get title based on drill level
        const getTitle = () => {
          if (drillState.level === 'year' && productTrendData) {
            return `${getName(productTrendData.title)} - ${productTrendData.periodLabel || selectedYear}`;
          }
          if (drillState.level === 'month' && dailyTrendData) {
            return getName(dailyTrendData.title);
          }
          if (drillState.level === 'day' && hourlyTrendData) {
            return getName(hourlyTrendData.title);
          }
          return currentLang === 'th' ? 'แนวโน้มการขายสินค้า' : 'Product Sales Trend';
        };

        // Get average line value
        const getAverage = () => {
          if (drillState.level === 'year' && productTrendData) return productTrendData.average;
          if (drillState.level === 'month' && dailyTrendData) return dailyTrendData.average;
          if (drillState.level === 'day' && hourlyTrendData) return hourlyTrendData.average;
          return 0;
        };

        // Check if can drill further
        const canDrillDown = drillState.level !== 'day';

        return (
          <div className="space-y-6">
            {/* Breadcrumb Navigation */}
            <div className="flex items-center gap-2 text-sm">
              {drillState.level !== 'year' && (
                <button
                  onClick={handleDrillUp}
                  className="p-1 rounded hover:bg-accent"
                  title={currentLang === 'th' ? 'กลับ' : 'Back'}
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
              )}
              <button
                onClick={() => navigateToDrillLevel('year')}
                className={`hover:underline ${drillState.level === 'year' ? 'font-semibold text-primary' : 'text-muted-foreground'}`}
              >
                {productTrendData?.periodLabel || selectedYear}
              </button>
              {drillState.selectedMonth !== null && (
                <>
                  <ChevronRight className="h-3 w-3 text-muted-foreground" />
                  <button
                    onClick={() => navigateToDrillLevel('month')}
                    className={`hover:underline ${drillState.level === 'month' ? 'font-semibold text-primary' : 'text-muted-foreground'}`}
                  >
                    {currentLang === 'th' ? MONTH_NAMES_TH[drillState.selectedMonth] : MONTH_NAMES_EN[drillState.selectedMonth]}
                  </button>
                </>
              )}
              {drillState.selectedDay !== null && (
                <>
                  <ChevronRight className="h-3 w-3 text-muted-foreground" />
                  <span className="font-semibold text-primary">
                    {currentLang === 'th' ? `วันที่ ${drillState.selectedDay}` : `Day ${drillState.selectedDay}`}
                  </span>
                </>
              )}
              {canDrillDown && (
                <span className="ml-2 text-xs text-muted-foreground flex items-center gap-1">
                  <ZoomIn className="h-3 w-3" />
                  {currentLang === 'th' ? 'คลิกเพื่อเลือก, ดับเบิลคลิกเพื่อดูรายละเอียด' : 'Click to select, double-click to drill down'}
                </span>
              )}
            </div>

            {/* Title */}
            <div className="text-lg font-semibold">
              {getTitle()}
            </div>

            {/* Interactive Legend */}
            <div className="flex items-center justify-center gap-6 mb-2">
              <button
                onClick={() => toggleLineVisibility('sales')}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm transition-all ${
                  visibleLines.sales
                    ? 'bg-blue-100 text-blue-700 border border-blue-300'
                    : 'bg-gray-100 text-gray-400 border border-gray-200 line-through'
                }`}
              >
                <span className={`w-3 h-3 rounded-full ${visibleLines.sales ? 'bg-blue-500' : 'bg-gray-300'}`} />
                {currentLang === 'th' ? 'ยอดขาย' : 'Sales'}
              </button>
              <button
                onClick={() => toggleLineVisibility('target')}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm transition-all ${
                  visibleLines.target
                    ? 'bg-green-100 text-green-700 border border-green-300'
                    : 'bg-gray-100 text-gray-400 border border-gray-200 line-through'
                }`}
              >
                <span className={`w-3 h-3 rounded-full ${visibleLines.target ? 'bg-green-500' : 'bg-gray-300'}`} />
                {currentLang === 'th' ? 'เป้าหมาย' : 'Target'}
              </button>
              <button
                onClick={() => toggleLineVisibility('average')}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm transition-all ${
                  visibleLines.average
                    ? 'bg-red-100 text-red-700 border border-red-300'
                    : 'bg-gray-100 text-gray-400 border border-gray-200 line-through'
                }`}
              >
                <span className={`w-3 h-3 rounded-full ${visibleLines.average ? 'bg-red-500' : 'bg-gray-300'}`} />
                {currentLang === 'th' ? 'ค่าเฉลี่ย' : 'Average'}
              </button>
            </div>

            {/* Line Chart with Click Handler */}
            <div className="bg-card rounded-lg border p-4">
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={chartConfig.data}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey={chartConfig.xKey} tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip
                    content={({ active, payload, label }) => {
                      if (!active || !payload?.length) return null;
                      const data = payload[0]?.payload;
                      if (!data) return null;
                      return (
                        <div className="bg-background border rounded-lg shadow-lg p-3 text-sm">
                          <div className="font-semibold mb-2">{label}</div>
                          {visibleLines.sales && (
                            <div className="text-blue-600">{currentLang === 'th' ? 'ยอดขาย' : 'Sales'}: {formatNumber(data.sales)}</div>
                          )}
                          {visibleLines.target && data.target !== undefined && (
                            <div className="text-green-600">{currentLang === 'th' ? 'เป้าหมาย' : 'Target'}: {formatNumber(data.target)}</div>
                          )}
                          <div className="text-muted-foreground">{currentLang === 'th' ? 'ออเดอร์' : 'Orders'}: {formatNumber(data.orders)}</div>
                          {data.revenue !== undefined && (
                            <div className="text-purple-600">{currentLang === 'th' ? 'รายได้' : 'Revenue'}: {formatCurrency(data.revenue)}</div>
                          )}
                          {canDrillDown && (
                            <div className="mt-2 pt-2 border-t text-xs text-muted-foreground flex items-center gap-1">
                              <ZoomIn className="h-3 w-3" />
                              {currentLang === 'th' ? 'ดับเบิลคลิกเพื่อดูรายละเอียด' : 'Double-click to drill down'}
                            </div>
                          )}
                        </div>
                      );
                    }}
                  />
                  {/* Target Line - rendered first so Sales line is on top for easier clicking */}
                  {visibleLines.target && (
                    <Line
                      type="monotone"
                      dataKey="target"
                      stroke="#22c55e"
                      strokeWidth={2}
                      strokeDasharray="5 5"
                      dot={{ r: 2, fill: '#22c55e' }}
                      activeDot={false}
                      name={currentLang === 'th' ? 'เป้าหมาย' : 'Target'}
                    />
                  )}
                  {/* Sales Line - rendered last to be on top, with larger clickable dots */}
                  {visibleLines.sales && (
                    <Line
                      type="monotone"
                      dataKey="sales"
                      stroke="#3b82f6"
                      strokeWidth={2}
                      dot={{ r: 5, cursor: 'pointer', strokeWidth: 2, fill: '#fff' }}
                      activeDot={{
                        r: 8,
                        cursor: 'pointer',
                        fill: '#3b82f6',
                        stroke: '#fff',
                        strokeWidth: 2,
                        onClick: (_: any, payload: any) => {
                          if (payload?.payload) {
                            const data = payload.payload;
                            const now = Date.now();

                            // Determine period index based on drill level
                            let periodIndex: number | null = null;
                            if (drillState.level === 'year' && data.trendIndex !== undefined) {
                              periodIndex = data.trendIndex;
                            } else if (drillState.level === 'year' && data.monthIndex !== undefined) {
                              periodIndex = data.monthIndex;
                            } else if (drillState.level === 'month' && data.day !== undefined) {
                              periodIndex = data.day - 1; // 0-indexed
                            } else if (drillState.level === 'day' && data.hour !== undefined) {
                              periodIndex = data.hour;
                            }

                            // Check for double-click: same point clicked within threshold
                            const lastClick = lastChartClickRef.current;
                            const isDoubleClick =
                              now - lastClick.timestamp < DOUBLE_CLICK_THRESHOLD &&
                              lastClick.periodIndex === periodIndex;

                            if (isDoubleClick && canDrillDown) {
                              // Double-click: drill down to next level
                              handleDrillDown(data);
                            } else {
                              // Single click: select period for table filtering
                              if (periodIndex !== null) {
                                setSelectedPeriodIndex(periodIndex);
                              }
                            }

                            // Update last click tracking
                            lastChartClickRef.current = {
                              timestamp: now,
                              periodIndex,
                            };
                          }
                        },
                      }}
                      name={currentLang === 'th' ? 'ยอดขาย' : 'Sales'}
                    />
                  )}
                  {/* Average Reference Line */}
                  {visibleLines.average && getAverage() > 0 && (
                    <ReferenceLine
                      y={getAverage()}
                      stroke="#ef4444"
                      strokeDasharray="3 3"
                      strokeWidth={1.5}
                      label={{ value: `Avg: ${formatNumber(getAverage())}`, position: 'insideTopRight', fill: '#ef4444', fontSize: 11 }}
                    />
                  )}
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* Summary Cards - Dynamic based on drill level */}
            {drillState.level === 'year' && productTrendData && (
              <>
                {/* Product Types Table with Expandable Categories */}
                <div className="bg-card rounded-lg border">
                  <div className="p-4 border-b flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <h3 className="font-medium">{currentLang === 'th' ? 'ประเภทสินค้า' : 'Product Types'}</h3>
                      {selectedPeriodIndex !== null && productTrendData.periodBreakdown?.[selectedPeriodIndex] && (
                        <>
                          <span className="inline-flex items-center gap-2 px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs">
                            {currentLang === 'th' ? 'แสดง:' : 'Viewing:'} {productTrendData.periodBreakdown[selectedPeriodIndex].periodLabel}
                            <button onClick={clearPeriodSelection} className="hover:bg-blue-200 rounded-full p-0.5">
                              <X className="h-3 w-3" />
                            </button>
                          </span>
                          <button
                            onClick={() => {
                              // Get the actual month data from the trend at this index
                              const trendPoint = productTrendData.trend[selectedPeriodIndex];
                              handleDrillDown({
                                monthIndex: trendPoint?.monthIndex ?? selectedPeriodIndex,
                                monthYear: trendPoint?.monthYear,
                              });
                            }}
                            className="inline-flex items-center gap-1 px-3 py-1 bg-blue-600 text-white rounded-full text-xs hover:bg-blue-700 transition-colors"
                          >
                            <ZoomIn className="h-3 w-3" />
                            {currentLang === 'th' ? 'ดูรายวัน' : 'View Daily'}
                          </button>
                        </>
                      )}
                    </div>
                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1"><Check className="h-3 w-3 text-green-600" /> {currentLang === 'th' ? 'บรรลุเป้า' : 'Target Met'}</span>
                      <span className="flex items-center gap-1"><X className="h-3 w-3 text-red-600" /> {currentLang === 'th' ? 'ไม่ถึงเป้า' : 'Below Target'}</span>
                    </div>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b bg-muted/30">
                          <th className="text-left py-3 px-4 font-medium">{currentLang === 'th' ? 'ประเภท' : 'Type'}</th>
                          <th className="text-right py-3 px-4 font-medium">{currentLang === 'th' ? 'ยอดขาย' : 'Sales'}</th>
                          <th className="text-right py-3 px-4 font-medium">{currentLang === 'th' ? 'รายได้' : 'Revenue'}</th>
                          <th className="text-right py-3 px-4 font-medium">{currentLang === 'th' ? 'เป้าหมาย' : 'Target'}</th>
                          <th className="text-right py-3 px-4 font-medium">{currentLang === 'th' ? 'บรรลุ' : 'Achievement'}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(() => {
                          const data = selectedPeriodIndex !== null && productTrendData.periodBreakdown?.[selectedPeriodIndex]
                            ? productTrendData.periodBreakdown[selectedPeriodIndex].productTypes
                            : productTrendData.productTypes;
                          return data.map((category) => (
                            <>
                              {/* Category Row */}
                              <tr
                                key={category.name}
                                className="border-b cursor-pointer hover:bg-muted/20"
                                onClick={() => toggleProductCategory(category.name)}
                              >
                                <td className="py-3 px-4 font-medium">
                                  <div className="flex items-center gap-2">
                                    {category.products && category.products.length > 0 ? (
                                      expandedProductCategories.has(category.name)
                                        ? <ChevronDown className="h-4 w-4 text-muted-foreground" />
                                        : <ChevronRight className="h-4 w-4 text-muted-foreground" />
                                    ) : <span className="w-4" />}
                                    {category.name}
                                  </div>
                                </td>
                                <td className="py-3 px-4 text-right">{formatNumber(category.sales)}</td>
                                <td className="py-3 px-4 text-right text-green-600">{formatCurrency(category.revenue)}</td>
                                <td className="py-3 px-4 text-right text-muted-foreground">{formatNumber(category.target)}</td>
                                <td className="py-3 px-4 text-right">
                                  <AchievementBadge value={category.achievementPct} />
                                </td>
                              </tr>
                              {/* Product Rows (when expanded) */}
                              {expandedProductCategories.has(category.name) && category.products?.map((product) => (
                                <tr key={product.id} className="border-b bg-muted/5">
                                  <td className="py-2 px-4 pl-10 text-muted-foreground">{product.name}</td>
                                  <td className="py-2 px-4 text-right text-sm">{formatNumber(product.sales)}</td>
                                  <td className="py-2 px-4 text-right text-sm text-green-600">{formatCurrency(product.revenue)}</td>
                                  <td className="py-2 px-4 text-right text-sm text-muted-foreground">{formatNumber(product.target)}</td>
                                  <td className="py-2 px-4 text-right">
                                    <AchievementBadge value={product.achievementPct} size="sm" />
                                  </td>
                                </tr>
                              ))}
                            </>
                          ));
                        })()}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Year Summary */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-card rounded-lg border p-4 text-center">
                    <div className="text-2xl font-bold text-blue-600">{formatNumber(productTrendData.summary.totalSales)}</div>
                    <div className="text-sm text-muted-foreground">{currentLang === 'th' ? 'ยอดขายรวม' : 'Total Sales'}</div>
                  </div>
                  <div className="bg-card rounded-lg border p-4 text-center">
                    <div className="text-2xl font-bold">{productTrendData.summary.categoryCount}</div>
                    <div className="text-sm text-muted-foreground">{currentLang === 'th' ? 'จำนวนหมวดหมู่' : 'Categories'}</div>
                  </div>
                  <div className="bg-card rounded-lg border p-4 text-center">
                    <div className="text-2xl font-bold text-green-600">{formatNumber(productTrendData.summary.averagePerCategory)}</div>
                    <div className="text-sm text-muted-foreground">{currentLang === 'th' ? 'เฉลี่ยต่อหมวด' : 'Avg per Category'}</div>
                  </div>
                </div>
              </>
            )}

            {/* Month (Daily) Summary */}
            {drillState.level === 'month' && dailyTrendData && (
              <>
                {/* Summary Cards */}
                <div className="grid grid-cols-5 gap-4">
                  <div className="bg-card rounded-lg border p-4 text-center">
                    <div className="text-2xl font-bold text-blue-600">{formatNumber(dailyTrendData.summary.totalSales)}</div>
                    <div className="text-sm text-muted-foreground">{currentLang === 'th' ? 'ยอดขายรวม' : 'Total Sales'}</div>
                  </div>
                  <div className="bg-card rounded-lg border p-4 text-center">
                    <div className="text-2xl font-bold text-green-600">{formatNumber(dailyTrendData.summary.totalTarget)}</div>
                    <div className="text-sm text-muted-foreground">{currentLang === 'th' ? 'เป้าหมาย' : 'Target'}</div>
                  </div>
                  <div className="bg-card rounded-lg border p-4 text-center">
                    <div className={`text-2xl font-bold ${dailyTrendData.summary.achievementPercentage >= 100 ? 'text-green-600' : dailyTrendData.summary.achievementPercentage >= 80 ? 'text-yellow-600' : 'text-red-600'}`}>
                      {dailyTrendData.summary.achievementPercentage}%
                    </div>
                    <div className="text-sm text-muted-foreground">{currentLang === 'th' ? 'บรรลุเป้า' : 'Achievement'}</div>
                  </div>
                  <div className="bg-card rounded-lg border p-4 text-center">
                    <div className="text-2xl font-bold text-purple-600">{formatCurrency(dailyTrendData.summary.totalRevenue)}</div>
                    <div className="text-sm text-muted-foreground">{currentLang === 'th' ? 'รายได้รวม' : 'Total Revenue'}</div>
                  </div>
                  <div className="bg-card rounded-lg border p-4 text-center">
                    <div className="text-2xl font-bold text-orange-600">
                      {currentLang === 'th' ? `วันที่ ${dailyTrendData.summary.peakDay.day}` : `Day ${dailyTrendData.summary.peakDay.day}`}
                    </div>
                    <div className="text-sm text-muted-foreground">{currentLang === 'th' ? 'วันขายสูงสุด' : 'Peak Day'}</div>
                  </div>
                </div>

                {/* Product Types Table with Expandable Categories (Daily) */}
                {dailyTrendData.productTypes && dailyTrendData.productTypes.length > 0 && (
                  <div className="bg-card rounded-lg border">
                    <div className="p-4 border-b flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <h3 className="font-medium">{currentLang === 'th' ? 'ประเภทสินค้า' : 'Product Types'}</h3>
                        {selectedPeriodIndex !== null && dailyTrendData.periodBreakdown?.[selectedPeriodIndex] && (
                          <>
                            <span className="inline-flex items-center gap-2 px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs">
                              {currentLang === 'th' ? 'แสดง:' : 'Viewing:'} {dailyTrendData.periodBreakdown[selectedPeriodIndex].periodLabel}
                              <button onClick={clearPeriodSelection} className="hover:bg-blue-200 rounded-full p-0.5">
                                <X className="h-3 w-3" />
                              </button>
                            </span>
                            <button
                              onClick={() => {
                                handleDrillDown({ day: selectedPeriodIndex + 1 }); // day is 1-indexed
                              }}
                              className="inline-flex items-center gap-1 px-3 py-1 bg-blue-600 text-white rounded-full text-xs hover:bg-blue-700 transition-colors"
                            >
                              <ZoomIn className="h-3 w-3" />
                              {currentLang === 'th' ? 'ดูรายชั่วโมง' : 'View Hourly'}
                            </button>
                          </>
                        )}
                      </div>
                      <div className="flex items-center gap-4 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1"><Check className="h-3 w-3 text-green-600" /> {currentLang === 'th' ? 'บรรลุเป้า' : 'Target Met'}</span>
                        <span className="flex items-center gap-1"><X className="h-3 w-3 text-red-600" /> {currentLang === 'th' ? 'ไม่ถึงเป้า' : 'Below Target'}</span>
                      </div>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b bg-muted/30">
                            <th className="text-left py-3 px-4 font-medium">{currentLang === 'th' ? 'ประเภท' : 'Type'}</th>
                            <th className="text-right py-3 px-4 font-medium">{currentLang === 'th' ? 'ยอดขาย' : 'Sales'}</th>
                            <th className="text-right py-3 px-4 font-medium">{currentLang === 'th' ? 'รายได้' : 'Revenue'}</th>
                            <th className="text-right py-3 px-4 font-medium">{currentLang === 'th' ? 'เป้าหมาย' : 'Target'}</th>
                            <th className="text-right py-3 px-4 font-medium">{currentLang === 'th' ? 'บรรลุ' : 'Achievement'}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(() => {
                            const data = selectedPeriodIndex !== null && dailyTrendData.periodBreakdown?.[selectedPeriodIndex]
                              ? dailyTrendData.periodBreakdown[selectedPeriodIndex].productTypes
                              : dailyTrendData.productTypes;
                            return data.map((category) => (
                              <>
                                {/* Category Row */}
                                <tr
                                  key={category.name}
                                  className="border-b cursor-pointer hover:bg-muted/20"
                                  onClick={() => toggleProductCategory(category.name)}
                                >
                                  <td className="py-3 px-4 font-medium">
                                    <div className="flex items-center gap-2">
                                      {category.products && category.products.length > 0 ? (
                                        expandedProductCategories.has(category.name)
                                          ? <ChevronDown className="h-4 w-4 text-muted-foreground" />
                                          : <ChevronRight className="h-4 w-4 text-muted-foreground" />
                                      ) : <span className="w-4" />}
                                      {category.name}
                                    </div>
                                  </td>
                                  <td className="py-3 px-4 text-right">{formatNumber(category.sales)}</td>
                                  <td className="py-3 px-4 text-right text-green-600">{formatCurrency(category.revenue)}</td>
                                  <td className="py-3 px-4 text-right text-muted-foreground">{formatNumber(category.target)}</td>
                                  <td className="py-3 px-4 text-right">
                                    <AchievementBadge value={category.achievementPct} />
                                  </td>
                                </tr>
                                {/* Product Rows (when expanded) */}
                                {expandedProductCategories.has(category.name) && category.products?.map((product) => (
                                  <tr key={product.id} className="border-b bg-muted/5">
                                    <td className="py-2 px-4 pl-10 text-muted-foreground">{product.name}</td>
                                    <td className="py-2 px-4 text-right text-sm">{formatNumber(product.sales)}</td>
                                    <td className="py-2 px-4 text-right text-sm text-green-600">{formatCurrency(product.revenue)}</td>
                                    <td className="py-2 px-4 text-right text-sm text-muted-foreground">{formatNumber(product.target)}</td>
                                    <td className="py-2 px-4 text-right">
                                      <AchievementBadge value={product.achievementPct} size="sm" />
                                    </td>
                                  </tr>
                                ))}
                              </>
                            ));
                          })()}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Day of Week Breakdown Table */}
                {dailyTrendData.dayOfWeekBreakdown && dailyTrendData.dayOfWeekBreakdown.length > 0 && (
                  <div className="bg-card rounded-lg border">
                    <div className="p-4 border-b">
                      <h3 className="font-medium">{currentLang === 'th' ? 'ยอดขายตามวันในสัปดาห์' : 'Sales by Day of Week'}</h3>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b bg-muted/30">
                            <th className="text-left py-3 px-4 font-medium">{currentLang === 'th' ? 'วัน' : 'Day'}</th>
                            <th className="text-right py-3 px-4 font-medium">{currentLang === 'th' ? 'ยอดขาย' : 'Sales'}</th>
                            <th className="text-right py-3 px-4 font-medium">{currentLang === 'th' ? 'ออเดอร์' : 'Orders'}</th>
                            <th className="text-right py-3 px-4 font-medium">{currentLang === 'th' ? 'รายได้' : 'Revenue'}</th>
                            <th className="text-right py-3 px-4 font-medium">{currentLang === 'th' ? 'เฉลี่ย/วัน' : 'Avg/Day'}</th>
                            <th className="text-right py-3 px-4 font-medium">{currentLang === 'th' ? 'จำนวนวัน' : 'Days'}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {dailyTrendData.dayOfWeekBreakdown.map((item, index) => (
                            <tr key={item.dayOfWeek} className={`border-b ${index % 2 === 0 ? 'bg-background' : 'bg-muted/10'}`}>
                              <td className="py-3 px-4 font-medium">{item.dayOfWeek}</td>
                              <td className="py-3 px-4 text-right text-blue-600">{formatNumber(item.totalSales)}</td>
                              <td className="py-3 px-4 text-right">{formatNumber(item.totalOrders)}</td>
                              <td className="py-3 px-4 text-right text-green-600">{formatCurrency(item.totalRevenue)}</td>
                              <td className="py-3 px-4 text-right text-muted-foreground">{formatNumber(item.averageSales)}</td>
                              <td className="py-3 px-4 text-right">{item.daysCount}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Day (Hourly) Summary */}
            {drillState.level === 'day' && hourlyTrendData && (
              <>
                {/* Summary Cards */}
                <div className="grid grid-cols-5 gap-4">
                  <div className="bg-card rounded-lg border p-4 text-center">
                    <div className="text-2xl font-bold text-blue-600">{formatNumber(hourlyTrendData.summary.totalSales)}</div>
                    <div className="text-sm text-muted-foreground">{currentLang === 'th' ? 'ยอดขายรวม' : 'Total Sales'}</div>
                  </div>
                  <div className="bg-card rounded-lg border p-4 text-center">
                    <div className="text-2xl font-bold text-green-600">{formatNumber(hourlyTrendData.summary.totalTarget)}</div>
                    <div className="text-sm text-muted-foreground">{currentLang === 'th' ? 'เป้าหมาย' : 'Target'}</div>
                  </div>
                  <div className="bg-card rounded-lg border p-4 text-center">
                    <div className={`text-2xl font-bold ${hourlyTrendData.summary.achievementPercentage >= 100 ? 'text-green-600' : hourlyTrendData.summary.achievementPercentage >= 80 ? 'text-yellow-600' : 'text-red-600'}`}>
                      {hourlyTrendData.summary.achievementPercentage}%
                    </div>
                    <div className="text-sm text-muted-foreground">{currentLang === 'th' ? 'บรรลุเป้า' : 'Achievement'}</div>
                  </div>
                  <div className="bg-card rounded-lg border p-4 text-center">
                    <div className="text-2xl font-bold text-purple-600">{formatCurrency(hourlyTrendData.summary.totalRevenue)}</div>
                    <div className="text-sm text-muted-foreground">{currentLang === 'th' ? 'รายได้รวม' : 'Total Revenue'}</div>
                  </div>
                  <div className="bg-card rounded-lg border p-4 text-center">
                    <div className="text-2xl font-bold text-orange-600">{hourlyTrendData.summary.peakHour.label}</div>
                    <div className="text-sm text-muted-foreground">{currentLang === 'th' ? 'ชั่วโมงขายสูงสุด' : 'Peak Hour'}</div>
                  </div>
                </div>

                {/* Product Types Table with Expandable Categories (Hourly) */}
                {hourlyTrendData.productTypes && hourlyTrendData.productTypes.length > 0 && (
                  <div className="bg-card rounded-lg border">
                    <div className="p-4 border-b flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <h3 className="font-medium">{currentLang === 'th' ? 'ประเภทสินค้า' : 'Product Types'}</h3>
                        {selectedPeriodIndex !== null && hourlyTrendData.periodBreakdown?.[selectedPeriodIndex] && (
                          <span className="inline-flex items-center gap-2 px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs">
                            {currentLang === 'th' ? 'แสดง:' : 'Viewing:'} {hourlyTrendData.periodBreakdown[selectedPeriodIndex].periodLabel}
                            <button onClick={clearPeriodSelection} className="hover:bg-blue-200 rounded-full p-0.5">
                              <X className="h-3 w-3" />
                            </button>
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-4 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1"><Check className="h-3 w-3 text-green-600" /> {currentLang === 'th' ? 'บรรลุเป้า' : 'Target Met'}</span>
                        <span className="flex items-center gap-1"><X className="h-3 w-3 text-red-600" /> {currentLang === 'th' ? 'ไม่ถึงเป้า' : 'Below Target'}</span>
                      </div>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b bg-muted/30">
                            <th className="text-left py-3 px-4 font-medium">{currentLang === 'th' ? 'ประเภท' : 'Type'}</th>
                            <th className="text-right py-3 px-4 font-medium">{currentLang === 'th' ? 'ยอดขาย' : 'Sales'}</th>
                            <th className="text-right py-3 px-4 font-medium">{currentLang === 'th' ? 'รายได้' : 'Revenue'}</th>
                            <th className="text-right py-3 px-4 font-medium">{currentLang === 'th' ? 'เป้าหมาย' : 'Target'}</th>
                            <th className="text-right py-3 px-4 font-medium">{currentLang === 'th' ? 'บรรลุ' : 'Achievement'}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(() => {
                            const data = selectedPeriodIndex !== null && hourlyTrendData.periodBreakdown?.[selectedPeriodIndex]
                              ? hourlyTrendData.periodBreakdown[selectedPeriodIndex].productTypes
                              : hourlyTrendData.productTypes;
                            return data.map((category) => (
                              <>
                                {/* Category Row */}
                                <tr
                                  key={category.name}
                                  className="border-b cursor-pointer hover:bg-muted/20"
                                  onClick={() => toggleProductCategory(category.name)}
                                >
                                  <td className="py-3 px-4 font-medium">
                                    <div className="flex items-center gap-2">
                                      {category.products && category.products.length > 0 ? (
                                        expandedProductCategories.has(category.name)
                                          ? <ChevronDown className="h-4 w-4 text-muted-foreground" />
                                          : <ChevronRight className="h-4 w-4 text-muted-foreground" />
                                      ) : <span className="w-4" />}
                                      {category.name}
                                    </div>
                                  </td>
                                  <td className="py-3 px-4 text-right">{formatNumber(category.sales)}</td>
                                  <td className="py-3 px-4 text-right text-green-600">{formatCurrency(category.revenue)}</td>
                                  <td className="py-3 px-4 text-right text-muted-foreground">{formatNumber(category.target)}</td>
                                  <td className="py-3 px-4 text-right">
                                    <AchievementBadge value={category.achievementPct} />
                                  </td>
                                </tr>
                                {/* Product Rows (when expanded) */}
                                {expandedProductCategories.has(category.name) && category.products?.map((product) => (
                                  <tr key={product.id} className="border-b bg-muted/5">
                                    <td className="py-2 px-4 pl-10 text-muted-foreground">{product.name}</td>
                                    <td className="py-2 px-4 text-right text-sm">{formatNumber(product.sales)}</td>
                                    <td className="py-2 px-4 text-right text-sm text-green-600">{formatCurrency(product.revenue)}</td>
                                    <td className="py-2 px-4 text-right text-sm text-muted-foreground">{formatNumber(product.target)}</td>
                                    <td className="py-2 px-4 text-right">
                                      <AchievementBadge value={product.achievementPct} size="sm" />
                                    </td>
                                  </tr>
                                ))}
                              </>
                            ));
                          })()}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Time Period Breakdown Table */}
                {hourlyTrendData.timePeriodBreakdown && hourlyTrendData.timePeriodBreakdown.length > 0 && (
                  <div className="bg-card rounded-lg border">
                    <div className="p-4 border-b">
                      <h3 className="font-medium">{currentLang === 'th' ? 'ยอดขายตามช่วงเวลา' : 'Sales by Time Period'}</h3>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b bg-muted/30">
                            <th className="text-left py-3 px-4 font-medium">{currentLang === 'th' ? 'ช่วงเวลา' : 'Period'}</th>
                            <th className="text-right py-3 px-4 font-medium">{currentLang === 'th' ? 'ยอดขาย' : 'Sales'}</th>
                            <th className="text-right py-3 px-4 font-medium">{currentLang === 'th' ? 'ออเดอร์' : 'Orders'}</th>
                            <th className="text-right py-3 px-4 font-medium">{currentLang === 'th' ? 'รายได้' : 'Revenue'}</th>
                            <th className="text-right py-3 px-4 font-medium">{currentLang === 'th' ? 'เฉลี่ย/ชม.' : 'Avg/Hour'}</th>
                            <th className="text-right py-3 px-4 font-medium">{currentLang === 'th' ? 'ชั่วโมงพีค' : 'Peak Hour'}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {hourlyTrendData.timePeriodBreakdown.map((item, index) => (
                            <tr key={item.period} className={`border-b ${index % 2 === 0 ? 'bg-background' : 'bg-muted/10'}`}>
                              <td className="py-3 px-4 font-medium">{currentLang === 'th' ? item.periodName.th : item.periodName.en}</td>
                              <td className="py-3 px-4 text-right text-blue-600">{formatNumber(item.totalSales)}</td>
                              <td className="py-3 px-4 text-right">{formatNumber(item.totalOrders)}</td>
                              <td className="py-3 px-4 text-right text-green-600">{formatCurrency(item.totalRevenue)}</td>
                              <td className="py-3 px-4 text-right text-muted-foreground">{formatNumber(item.averageSales)}</td>
                              <td className="py-3 px-4 text-right text-orange-600">{item.peakHour} ({formatNumber(item.peakSales)})</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        );
      }

      // ================================
      // REPORT 2: Product Hierarchy with Growth
      // ================================
      case 'product-hierarchy':
        if (!hierarchyData) {
          return <div className="py-8 text-center text-muted-foreground">{isLoading ? 'Loading...' : 'No data'}</div>;
        }

        return (
          <div className="space-y-6">
            {/* Title */}
            <div className="flex items-center justify-between">
              <div className="text-lg font-semibold">{getName(hierarchyData.title)}</div>
              <div className="text-sm text-muted-foreground">
                {hierarchyData.period.current} vs {hierarchyData.period.previous}
              </div>
            </div>

            {/* Summary */}
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-card rounded-lg border p-4 text-center">
                <div className="text-2xl font-bold text-blue-600">{formatNumber(hierarchyData.summary.totalSales)}</div>
                <div className="text-sm text-muted-foreground">{currentLang === 'th' ? 'เดือนนี้' : 'This Month'}</div>
              </div>
              <div className="bg-card rounded-lg border p-4 text-center">
                <div className="text-2xl font-bold text-gray-500">{formatNumber(hierarchyData.summary.totalPrevMonthSales)}</div>
                <div className="text-sm text-muted-foreground">{currentLang === 'th' ? 'เดือนที่แล้ว' : 'Last Month'}</div>
              </div>
              <div className="bg-card rounded-lg border p-4 text-center">
                <GrowthIndicator growth={hierarchyData.summary.growth} />
                <div className="text-sm text-muted-foreground">{currentLang === 'th' ? 'การเติบโต' : 'Growth'}</div>
              </div>
            </div>

            {/* Hierarchy Table */}
            <div className="bg-card rounded-lg border">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-muted/30">
                      <th className="text-left py-3 px-4 font-medium">{currentLang === 'th' ? 'หมวดหมู่ / สินค้า' : 'Category / Product'}</th>
                      <th className="text-right py-3 px-4 font-medium">{currentLang === 'th' ? 'เดือนนี้' : 'This Month'}</th>
                      <th className="text-right py-3 px-4 font-medium">{currentLang === 'th' ? 'เดือนที่แล้ว' : 'Last Month'}</th>
                      <th className="text-right py-3 px-4 font-medium">{currentLang === 'th' ? 'การเติบโต' : 'Growth'}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {hierarchyData.hierarchy.map((category) => (
                      <>
                        {/* Category Row */}
                        <tr
                          key={category.id}
                          className="border-b bg-muted/20 cursor-pointer hover:bg-muted/30"
                          onClick={() => toggleCategory(category.id)}
                        >
                          <td className="py-3 px-4 font-medium">
                            <div className="flex items-center gap-2">
                              {expandedCategories.has(category.id) ? (
                                <ChevronDown className="h-4 w-4" />
                              ) : (
                                <ChevronRight className="h-4 w-4" />
                              )}
                              {getName(category.name)}
                            </div>
                          </td>
                          <td className="py-3 px-4 text-right font-semibold">{formatNumber(category.sales)}</td>
                          <td className="py-3 px-4 text-right text-muted-foreground">{formatNumber(category.prevMonthSales)}</td>
                          <td className="py-3 px-4 text-right">
                            <GrowthIndicator growth={category.growth} />
                          </td>
                        </tr>
                        {/* Product Rows */}
                        {expandedCategories.has(category.id) && category.children.map((product) => (
                          <tr key={product.id} className="border-b bg-background">
                            <td className="py-2 px-4 pl-10 text-muted-foreground">{getName(product.name)}</td>
                            <td className="py-2 px-4 text-right">{formatNumber(product.sales)}</td>
                            <td className="py-2 px-4 text-right text-muted-foreground">{formatNumber(product.prevMonthSales)}</td>
                            <td className="py-2 px-4 text-right">
                              <GrowthIndicator growth={product.growth} />
                            </td>
                          </tr>
                        ))}
                      </>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        );

      // ================================
      // REPORT 3: Regional/District Sales Analysis
      // ================================
      case 'regional-sales':
        if (!isHQ) {
          return (
            <div className="py-8 text-center text-muted-foreground">
              {currentLang === 'th' ? 'รายงานนี้สำหรับ HQ เท่านั้น' : 'This report is for HQ Admin only'}
            </div>
          );
        }

        if (!regionalData) {
          return <div className="py-8 text-center text-muted-foreground">{isLoading ? 'Loading...' : 'No data'}</div>;
        }

        return (
          <div className="space-y-6">
            {/* Title */}
            <div className="text-lg font-semibold">
              {getName(regionalData.title)} - {regionalData.year}
            </div>

            {/* Line Chart */}
            <div className="bg-card rounded-lg border p-4">
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={regionalData.trend}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip formatter={(value) => [formatNumber(Number(value) || 0), 'Sales']} />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="sales"
                    stroke="#3b82f6"
                    strokeWidth={2}
                    dot={{ r: 4 }}
                    name={currentLang === 'th' ? 'ยอดขาย' : 'Sales'}
                  />
                  <ReferenceLine
                    y={regionalData.average}
                    stroke="#ef4444"
                    strokeDasharray="5 5"
                    label={{ value: `Avg: ${formatNumber(regionalData.average)}`, position: 'right', fill: '#ef4444' }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* Districts Table */}
            <div className="bg-card rounded-lg border">
              <div className="p-4 border-b">
                <h3 className="font-medium">{currentLang === 'th' ? 'ยอดขายตามเขต' : 'Sales by District'}</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-muted/30">
                      <th className="text-left py-3 px-4 font-medium">{currentLang === 'th' ? 'เขต' : 'District'}</th>
                      <th className="text-right py-3 px-4 font-medium">{currentLang === 'th' ? 'สาขา' : 'Branches'}</th>
                      <th className="text-right py-3 px-4 font-medium">{currentLang === 'th' ? 'เดือนนี้' : 'This Month'}</th>
                      <th className="text-right py-3 px-4 font-medium">{currentLang === 'th' ? 'เดือนที่แล้ว' : 'Last Month'}</th>
                      <th className="text-right py-3 px-4 font-medium">{currentLang === 'th' ? 'การเติบโต' : 'Growth'}</th>
                      <th className="text-center py-3 px-4 font-medium">{currentLang === 'th' ? 'สถานะ' : 'Status'}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {regionalData.districts.map((district, index) => (
                      <tr key={district.district} className={`border-b ${index % 2 === 0 ? 'bg-background' : 'bg-muted/10'}`}>
                        <td className="py-3 px-4 font-medium">{district.district}</td>
                        <td className="py-3 px-4 text-right">{district.branchCount}</td>
                        <td className="py-3 px-4 text-right font-semibold">{formatNumber(district.currentSales)}</td>
                        <td className="py-3 px-4 text-right text-muted-foreground">{formatNumber(district.prevMonthSales)}</td>
                        <td className="py-3 px-4 text-right">
                          <GrowthIndicator growth={district.growth} />
                        </td>
                        <td className="py-3 px-4 text-center">
                          <StatusIndicator status={district.status} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Summary */}
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-card rounded-lg border p-4 text-center">
                <div className="text-2xl font-bold">{regionalData.summary.totalDistricts}</div>
                <div className="text-sm text-muted-foreground">{currentLang === 'th' ? 'จำนวนเขต' : 'Districts'}</div>
              </div>
              <div className="bg-card rounded-lg border p-4 text-center">
                <div className="text-2xl font-bold">{regionalData.summary.totalBranches}</div>
                <div className="text-sm text-muted-foreground">{currentLang === 'th' ? 'จำนวนสาขา' : 'Branches'}</div>
              </div>
              <div className="bg-card rounded-lg border p-4 text-center">
                <div className="text-2xl font-bold text-green-600">{formatNumber(regionalData.summary.totalSales)}</div>
                <div className="text-sm text-muted-foreground">{currentLang === 'th' ? 'ยอดขายรวม' : 'Total Sales'}</div>
              </div>
            </div>
          </div>
        );

      // ================================
      // REPORT 4: Sales Target Dashboard
      // ================================
      case 'sales-target':
        if (!targetData) {
          return <div className="py-8 text-center text-muted-foreground">{isLoading ? 'Loading...' : 'No data'}</div>;
        }

        return (
          <div className="space-y-6">
            {/* Title */}
            <div className="flex items-center justify-between">
              <div className="text-lg font-semibold">
                {getName(targetData.title)} - {targetData.period.month} {targetData.period.year}
              </div>
              {/* District Filter (HQ only) */}
              {isHQ && targetData.filters.districts.length > 0 && (
                <select
                  value={selectedDistrict}
                  onChange={(e) => setSelectedDistrict(e.target.value)}
                  className="h-9 rounded-md border bg-background px-3 text-sm"
                >
                  <option value="">{currentLang === 'th' ? 'ทุกเขต' : 'All Districts'}</option>
                  {targetData.filters.districts.map((district) => (
                    <option key={district} value={district}>
                      {district}
                    </option>
                  ))}
                </select>
              )}
            </div>

            {/* KPI Cards */}
            <div className="grid grid-cols-4 gap-4">
              <div className="bg-card rounded-lg border p-4">
                <div className="text-sm text-muted-foreground mb-1">{currentLang === 'th' ? 'ยอดจริง' : 'Actual'}</div>
                <div className="text-2xl font-bold text-blue-600">{formatCurrency(targetData.summary.actual)}</div>
              </div>
              <div className="bg-card rounded-lg border p-4">
                <div className="text-sm text-muted-foreground mb-1">{currentLang === 'th' ? 'เป้าหมาย' : 'Target'}</div>
                <div className="text-2xl font-bold">{formatCurrency(targetData.summary.target)}</div>
              </div>
              <div className="bg-card rounded-lg border p-4">
                <div className="text-sm text-muted-foreground mb-1">{currentLang === 'th' ? 'บรรลุ %' : 'Achievement %'}</div>
                <div className="text-2xl font-bold">{targetData.summary.percentage}%</div>
              </div>
              <div className="bg-card rounded-lg border p-4">
                <div className="text-sm text-muted-foreground mb-1">{currentLang === 'th' ? 'สถานะ' : 'Status'}</div>
                <div className="flex items-center gap-2">
                  <AchievementStatus status={targetData.summary.status} />
                  <span className="font-medium capitalize">{targetData.summary.status}</span>
                </div>
              </div>
            </div>

            {/* Legend */}
            <div className="flex items-center gap-6 text-sm">
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-green-500" />
                <span>{getName(targetData.legend.high.label)}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-yellow-500" />
                <span>{getName(targetData.legend.medium.label)}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-red-500" />
                <span>{getName(targetData.legend.low.label)}</span>
              </div>
            </div>

            {/* Monthly Achievement Bar Chart */}
            <div className="bg-card rounded-lg border p-4">
              <h3 className="font-medium mb-4">{currentLang === 'th' ? 'ผลงานรายเดือน' : 'Monthly Achievement'}</h3>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={targetData.monthlyAchievement} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
                  <YAxis dataKey="month" type="category" width={80} tick={{ fontSize: 12 }} />
                  <Tooltip formatter={(value) => [`${Number(value || 0).toFixed(1)}%`, 'Achievement']} />
                  <Bar dataKey="percentage" name="Achievement %">
                    {targetData.monthlyAchievement.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={entry.status === 'high' ? '#22c55e' : entry.status === 'medium' ? '#eab308' : '#ef4444'}
                      />
                    ))}
                  </Bar>
                  <ReferenceLine x={75} stroke="#22c55e" strokeDasharray="3 3" />
                  <ReferenceLine x={51} stroke="#eab308" strokeDasharray="3 3" />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Branch Breakdown (HQ only) */}
            {isHQ && targetData.branchBreakdown.length > 0 && (
              <div className="bg-card rounded-lg border">
                <div className="p-4 border-b">
                  <h3 className="font-medium">{currentLang === 'th' ? 'รายละเอียดสาขา' : 'Branch Breakdown'}</h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-muted/30">
                        <th className="text-left py-3 px-4 font-medium">{currentLang === 'th' ? 'สาขา' : 'Branch'}</th>
                        <th className="text-left py-3 px-4 font-medium">{currentLang === 'th' ? 'เขต' : 'District'}</th>
                        <th className="text-right py-3 px-4 font-medium">{currentLang === 'th' ? 'ยอดจริง' : 'Actual'}</th>
                        <th className="text-right py-3 px-4 font-medium">{currentLang === 'th' ? 'เป้า' : 'Target'}</th>
                        <th className="text-right py-3 px-4 font-medium">{currentLang === 'th' ? 'บรรลุ %' : '%'}</th>
                        <th className="text-center py-3 px-4 font-medium">{currentLang === 'th' ? 'สถานะ' : 'Status'}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {targetData.branchBreakdown.map((branch, index) => (
                        <tr key={branch.branchId} className={`border-b ${index % 2 === 0 ? 'bg-background' : 'bg-muted/10'}`}>
                          <td className="py-3 px-4 font-medium">{branch.branchName}</td>
                          <td className="py-3 px-4 text-muted-foreground">{branch.district}</td>
                          <td className="py-3 px-4 text-right">{formatCurrency(branch.actual)}</td>
                          <td className="py-3 px-4 text-right text-muted-foreground">{formatCurrency(branch.target)}</td>
                          <td className="py-3 px-4 text-right font-semibold">{branch.percentage}%</td>
                          <td className="py-3 px-4 text-center">
                            <AchievementStatus status={branch.status} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  // Year options for tabs that still use plain year selection
  const yearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: 5 }, (_, i) => currentYear - i);
  }, []);

  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">
            {currentLang === 'th' ? 'วิเคราะห์ประสิทธิภาพ' : 'Performance Analytics'}
          </h1>
          <p className="text-muted-foreground text-sm">
            {currentLang === 'th' ? 'รายงานวิเคราะห์เชิงลึกสำหรับผู้บริหาร' : 'In-depth analytics reports for management'}
          </p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={isLoading}
          className="h-9 w-9 rounded-md border bg-background hover:bg-secondary flex items-center justify-center disabled:opacity-50"
          title="Refresh"
        >
          <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Filters Bar */}
      <div className="bg-card rounded-lg border p-4">
        <div className="flex flex-wrap items-center gap-4">
          {/* Period Filter for Product Trend */}
          {activeTab === 'product-trend' && (
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <select
                value={selectedPeriod}
                onChange={(e) => {
                  setSelectedPeriod(e.target.value);
                  // Reset drill-down when period changes
                  setDrillState({ level: 'year', selectedMonth: null, selectedMonthYear: null, selectedDay: null });
                  setSelectedPeriodIndex(null);
                }}
                className="h-9 rounded-md border bg-background px-3 text-sm"
              >
                <optgroup label={currentLang === 'th' ? 'ช่วงเวลา' : 'Time Period'}>
                  {periodOptions.filter(o => o.group === 'preset').map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {currentLang === 'th' ? opt.labelTh : opt.label}
                    </option>
                  ))}
                </optgroup>
                <optgroup label={currentLang === 'th' ? 'ปี' : 'Year'}>
                  {periodOptions.filter(o => o.group === 'year').map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {currentLang === 'th' ? opt.labelTh : opt.label}
                    </option>
                  ))}
                </optgroup>
              </select>
            </div>
          )}

          {/* Year Filter for other tabs */}
          {(activeTab === 'product-hierarchy' || activeTab === 'regional-sales') && (
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <select
                value={selectedYear}
                onChange={(e) => setSelectedPeriod(`year-${e.target.value}`)}
                className="h-9 rounded-md border bg-background px-3 text-sm"
              >
                {yearOptions.map((year) => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
          )}

          {/* Branch Filter */}
          {(activeTab === 'product-trend' || activeTab === 'product-hierarchy' || activeTab === 'sales-target') && isHQ && branches && (
            <div className="flex items-center gap-2">
              <Building2 className="h-4 w-4 text-muted-foreground" />
              <select
                value={selectedBranch}
                onChange={(e) => setSelectedBranch(e.target.value)}
                className="h-9 rounded-md border bg-background px-3 text-sm min-w-[180px]"
              >
                <option value="">{currentLang === 'th' ? 'ทุกสาขา' : 'All Branches'}</option>
                {branches.map((branch: any) => (
                  <option key={branch._id} value={branch._id}>
                    {branch.name}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      </div>

      {/* Report Tabs */}
      <div className="bg-card rounded-lg border">
        {/* Tab Navigation */}
        <div className="border-b overflow-x-auto overflow-y-hidden">
          <nav className="flex">
            {REPORT_TABS.map((tab) => {
              // Hide regional sales for non-HQ users
              if (tab.id === 'regional-sales' && !isHQ) return null;

              const Icon = tab.icon;
              const isActive = activeTab === tab.id;

              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                    isActive
                      ? 'border-primary text-primary bg-primary/5'
                      : 'border-transparent text-muted-foreground hover:text-foreground hover:bg-accent/50'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  <span>{currentLang === 'th' ? tab.labelTh : tab.label}</span>
                </button>
              );
            })}
          </nav>
        </div>

        {/* Report Content */}
        <div className="p-4">
          {renderReportContent()}
        </div>
      </div>
    </div>
  );
}

export default PerformanceAnalyticsPage;
